import Config from "@/core/misc/Config";
import AclService from "@/core/services/Acl/AclService";
import RpcRequest from "@/core/services/Rpc/RpcRequest";
import ApplicationError from "@/core/misc/ApplicationError";
import SentryEvent from "./SentryEvent";
import SentryBreadcrumbList from "./SentryBreadcrumbList";

/**
 * Сервис мониторинга ошибок Sentry
 * Лёгкая реализация без официального SDK
 */
class SentryService {

    public static isInitialized = false;

    private static readonly TIMEOUT_MS = 5000;

    private static readonly IGNORE_PATTERNS: Array<string | RegExp> = [
        'Network Error',
        'AbortError',
        'Script error',
        /ResizeObserver loop/,
        /ChunkLoadError/,
        /mc\.yandex\.ru/,
        /chrome-extension:/,
        /moz-extension:/,
        'AdBlock',
        'uBlock',
    ];

    /**
     * Принудительная группировка: события, сообщение которых совпало с паттерном,
     * собираются в одну issue с указанным fingerprint независимо от стека.
     * По умолчанию Sentry группирует по стеку, и одна и та же ошибка из разных
     * мест вызова (например, RPC из разных сервисов) расползается по разным issue.
     */
    private static readonly FINGERPRINT_PATTERNS: Array<{ pattern: string | RegExp; fingerprint: string }> = [
        // Одна и та же сетевая ошибка fetch, у каждого браузера свой текст:
        // Chrome/Edge, Firefox, Safari
        { pattern: 'Failed to fetch', fingerprint: 'load-failed' },
        { pattern: 'NetworkError when attempting to fetch resource', fingerprint: 'load-failed' },
        { pattern: 'Load failed', fingerprint: 'load-failed' },
        { pattern: 'New version detected, reloading', fingerprint: 'new-version-reload' },
        // Таймаут RPC — текст из RpcResponseError.fromTimeout()
        { pattern: 'Request timeout', fingerprint: 'request-timeout' },
    ];

    /**
     * Колбэк для обработки ApplicationError — передаётся из Core при инициализации.
     * Инверсия зависимостей: SentryService не импортирует Core.
     */
    static #handleApplicationError: ((error: ApplicationError) => void) | null = null;

    /**
     * Инициализация сервиса
     */
    static init(handleApplicationError?: (error: ApplicationError) => void): void {
        if (this.isInitialized) {
            console.warn('SentryService уже инициализирован');
            return;
        }

        if (!Config.sentry?.isEnabled) {
            console.debug('📊 Sentry отключен в конфигурации');
            return;
        }

        if (!Config.sentry?.publicKey || !Config.sentry?.host || !Config.sentry?.projectId || !Config.sentry?.organization) {
            console.error('❌ Неверный файл конфигурации: не настроены параметры Sentry (publicKey, host, projectId, organization)');
            return;
        }

        if (handleApplicationError) {
            this.#handleApplicationError = handleApplicationError;
        }

        this.#setupGlobalHandlers();
        this.isInitialized = true;
    }

    /**
     * Подключить Sentry к Vue (перехват ошибок компонентов)
     */
    static setupVueErrorHandler(app: any): void {
        if (!app || !this.isInitialized) return;

        app.config.errorHandler = (error: Error, instance: any, info: string) => {
            // ApplicationError обрабатываем отдельно — тост + Sentry по severity
            if (error instanceof ApplicationError && this.#handleApplicationError) {
                this.#handleApplicationError(error);
                return;
            }

            const componentName = instance?.$options?.name
                || instance?.$.type?.name
                || instance?.$.type?.__name
                || 'Unknown';

            this.reportError(error,
                { 'error.source': 'vue' },
                { vue: { componentName, lifecycleHook: info } },
                `Vue:${componentName}`
            );
        };
    }

    /**
     * Подключить Sentry к роутеру (отслеживание навигации)
     */
    static setupRouterTracking(router: any): void {
        if (!router || !this.isInitialized) return;

        router.afterEach((to: any, from: any) => {
            SentryBreadcrumbList.addNavigation(to, from);
        });
    }

    /**
     * Добавить breadcrumb диалога
     * @param id - id сущности, с которой открыт диалог (props.id), если есть
     */
    static trackDialog(dialogName: string, action: 'open' | 'close', id?: number | string): void {
        if (!this.isInitialized) return;
        SentryBreadcrumbList.addDialog(dialogName, action, id);
    }

    /**
     * Глобальные обработчики ошибок
     */
    static #setupGlobalHandlers(): void {
        window.addEventListener('unhandledrejection', event => {
            event.preventDefault();

            // ApplicationError обрабатываем отдельно — тост + Sentry по severity
            if (event.reason instanceof ApplicationError) {
                this.#handleApplicationError?.(event.reason);
                return;
            }

            if (event.reason instanceof Error) {
                this.reportError(event.reason);
            } else if (event.reason) {
                this.reportError(new Error(String(event.reason)));
            }
        });

        window.addEventListener('error', event => {
            event.preventDefault();

            if (event.error) {
                this.reportError(event.error);
            }
        });
    }

    /**
     * Отправить ошибку в Sentry.
     *
     * К ошибке автоматически прикрепляются все накопленные breadcrumb-крошки.
     * Крошки работают циклически — при переполнении старые вытесняются новыми,
     * поэтому в отчёт попадают только последние N действий пользователя перед ошибкой.
     *
     * @param error          — объект ошибки
     * @param tags           — теги для фильтрации в Sentry (domain, component)
     * @param contexts       — дополнительный контекст (состояние, параметры запроса)
     * @param label          — человекочитаемая метка для консоли
     * @param attachmentPayload — строка для прикрепления к событию (JSON тела запроса, ответ сервера)
     * @param debugData      — данные для отладки, выводятся только в консоль
     *
     * @example
     * // Простая отправка ошибки
     * SentryService.reportError(new Error('Не удалось загрузить данные'));
     *
     * @example
     * // С тегами и контекстом
     * SentryService.reportError(error, {
     *     domain: 'TransportOrders',
     *     component: 'TransportOrderCard',
     * }, {
     *     request: { method: 'transportOrders.get', params: { id: 123 } },
     * });
     *
     * @example
     * // С меткой и данными для отладки
     * SentryService.reportError(error, null, null, 'Ошибка сохранения ЗТК', null, {
     *     transportOrder,
     *     response,
     * });
     */
    static async reportError(
        error: Error,
        tags?: Record<string, string>,
        contexts?: Record<string, any>,
        label?: string,
        attachmentPayload?: string,
        debugData?: any
    ): Promise<void> {
        if (!this.isInitialized || !error || this.#isIgnorableError(error)) {
            return;
        }

        const event = SentryEvent.fromError(error, tags, contexts);
        event.attachmentPayload = attachmentPayload ?? null;

        event.setBreadcrumbs(SentryBreadcrumbList.getAll());
        event.setUser(AclService.currentUser?.id);
        event.fingerprint = this.#getFingerprint(error);

        this.#logErrorToConsole(error, label, debugData);

        try {
            const sent = await this.#sendToSentryServer(event);

            if (sent) {
                console.error(`📊 Error reported to Sentry: ${this.getEventUrl(event.id)}`);
            } else {
                console.warn('⚠️ Не удалось отправить ошибку в Sentry');
            }
        } catch (e) {
            console.error('❌ Ошибка отправки в Sentry:', e);
        }
    }

    /**
     * Отправить RPC ошибку
     */
    static reportRpcError(error: any, rpcRequest: RpcRequest | null): void {
        // Создаём Error для Sentry с оригинальным сообщением
        // new Error() здесь захватит стек вызова — откуда пришёл RPC запрос
        const displayError = new Error(error?.message || 'RPC Error');

        const sentryId = error?.sentryId;
        const hasValidSentryId = sentryId && !['disabled', 'sentry_error'].includes(sentryId);

        const tags: Record<string, string> = {
            'error.source': 'rpc',
            'rpc.method': rpcRequest?.method || 'unknown',
            'rpc.requestId': String(rpcRequest?.id || 'unknown'),
            'error.code': String(error?.code || 'unknown'),
        };

        if (hasValidSentryId) {
            tags['error.backendEventId'] = sentryId;
            tags['error.backendEventUrl'] = this.getEventUrl(sentryId);
        }

        if (rpcRequest?.meta?.sentryTraceId) {
            tags['trace.id'] = rpcRequest.meta.sentryTraceId;
        }

        const contexts: Record<string, any> = {};
        if (error?.debugData) {
            contexts['error.debugData'] = error.debugData;
        }

        // Payload уходит как attachment — Sentry не парсит, можно скачать целиком
        const attachmentPayload = rpcRequest
            ? JSON.stringify(rpcRequest.toSentryContext(), null, 2)
            : undefined;

        this.reportError(displayError, tags, contexts, `RPC:${rpcRequest?.method || '?'}`, attachmentPayload, error?.debugData);
    }

    /**
     * Отправить событие на сервер Sentry
     */
    static async #sendToSentryServer(event: SentryEvent): Promise<boolean> {
        const envelope = this.#buildEnvelope(event);
        const url = this.#getEnvelopeUrl();

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT_MS);

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: envelope,
                headers: { 'Content-Type': 'text/plain' },
                keepalive: true,
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            return response.ok;

        } catch {
            clearTimeout(timeoutId);

            // Fallback на sendBeacon при ошибке сети / таймауте
            // sendBeacon возвращает только факт постановки в очередь, не доставки
            const blob = new Blob([envelope], { type: 'text/plain' });
            return navigator.sendBeacon?.(url, blob) ?? false;
        }
    }

    /**
     * Вывод ошибки в консоль с отфильтрованным стеком
     */
    static #logErrorToConsole(error: Error, label?: string, debugData?: any): void {

        const prefix = label ? `[${label}]` : error.name;
        const frames = this.#parseStackForConsole(error);
        const isFirefox = navigator.userAgent.includes('Firefox');
        const isDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? true;

        // Заголовок — стандартный error
        console.error(`${prefix}: ${error.message}`);

        // прячем стек на всех окружениях кроме локального, потому что на остальных окружениях мы работает с минифицированным кодом
        // и трейс реально посмотреть только в сентри. Ну и если debug == false, тоже отключаем
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (!Config.debug || !isLocalhost) {
            return;
        }

        // Стек
        if (frames.length > 0) {
            const lines: string[] = [];
            const styles: string[] = [];

            // Пустая строка + сообщение
            if (isFirefox && isDark) {
                lines.push(`%c\n${error.message}\n`);
                styles.push('color: #888;');
            } else {
                lines.push(`%c\n${error.message}\n`);
                styles.push(isDark ? 'color: #ccc;' : 'color: #333;');
            }

            // Стек

            // Ширина колонки "файл:строка"
            const locationWidth = 30;

            frames.forEach(f => {
                const [file, line] = f.location.split(':');
                const location = `${file}:${line}`;
                // Пробелы для выравнивания колонки с именем функции
                const padding = ' '.repeat(Math.max(0, locationWidth - location.length));

                if (isFirefox && isDark) {
                    // Firefox тёмная: всё тёмно-серым
                    lines.push(`%c${file}:%c${line}${padding} %c${f.function}`);
                    styles.push('color: #888;', 'color: #888;', 'color: #888;');
                } else if (isDark) {
                    // Chrome тёмная тема: файл белый, строка жёлтая, функция серая
                    lines.push(`%c${file}:%c${line}${padding} %c${f.function}`);
                    styles.push('color: #fff;', 'color: #f1c40f;', 'color: #888;');
                } else {
                    // Светлая тема (Chrome и Firefox): файл чёрный, строка тёмный изумруд, функция серая
                    lines.push(`%c${file}:%c${line}${padding} %c${f.function}`);
                    styles.push('color: #000;', 'color: #046307;', 'color: #888;');
                }
            });

            // Пустая строка после
            lines.push('\u00A0');

            console.log(lines.join('\n'), ...styles);
        }

        if (debugData) {
            const style = isDark ? 'color: #ccc;' : 'color: #333;';
            console.log(`%c\ndebugData:`, style, debugData);
        }
    }

    /**
     * Парсинг стека для консольного вывода
     */
    static #parseStackForConsole(error: Error): Array<{ location: string; function: string; inApp: boolean }> {
        if (!error.stack) return [];

        const lines = error.stack.split('\n').slice(1);
        const frames: Array<{ location: string; function: string; inApp: boolean }> = [];

        // Chrome/Edge: "    at functionName (file:line:col)" или "    at file:line:col"
        const chromeRegex = /^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+)(?::\d+)?\)?$/;
        // Firefox: "functionName@file:line:col"
        const firefoxRegex = /^(.*)@(.+?):(\d+)(?::\d+)?$/;

        for (const line of lines) {
            let functionName: string;
            let fileName: string;
            let lineNumber: string;

            const chromeMatch = line.match(chromeRegex);
            const firefoxMatch = line.match(firefoxRegex);

            if (chromeMatch) {
                functionName = chromeMatch[1] || '<anonymous>';
                fileName = chromeMatch[2] || '';
                lineNumber = chromeMatch[3] || '0';
            } else if (firefoxMatch) {
                functionName = firefoxMatch[1] || '<anonymous>';
                fileName = firefoxMatch[2] || '';
                lineNumber = firefoxMatch[3] || '0';
            } else {
                continue;
            }

            // Фильтруем служебные файлы
            if (fileName.includes('node_modules')) continue;
            if (fileName.includes('.esm-bundler.')) continue;
            if (fileName.includes('SentryService')) continue;
            if (fileName.includes('RpcService')) continue;
            if (fileName.includes('Core.js')) continue;
            if (fileName.includes('Core.ts')) continue;
            if (!fileName) continue;

            const shortFileName = (fileName.split('/').pop() || fileName).split('?')[0];

            frames.push({
                location: `${shortFileName}:${lineNumber}`,
                function: functionName,
                inApp: true
            });

            if (frames.length >= 8) break;
        }

        return frames;
    }

    /**
     * Собрать Envelope — формат передачи событий в Sentry
     * Состоит из трёх строк: заголовок конверта, заголовок элемента, payload события
     */
    static #buildEnvelope(event: SentryEvent): string {
        const payload = event.toPayload();
        const payloadString = JSON.stringify(payload);

        const envelopeHeader = JSON.stringify({
            event_id: event.id,
            sent_at: new Date().toISOString(),
            dsn: this.#getDsn()
        });

        // length должен быть в байтах, не символах (важно для UTF-8)
        const payloadBytes = new TextEncoder().encode(payloadString).length;

        const itemHeader = JSON.stringify({
            type: 'event',
            length: payloadBytes
        });

        let envelope = `${envelopeHeader}\n${itemHeader}\n${payloadString}\n`;

        // Attachment: payload запроса как файл (Sentry не парсит, можно скачать целиком)
        if (event.attachmentPayload) {
            const attachmentBytes = new TextEncoder().encode(event.attachmentPayload).length;
            const attachmentHeader = JSON.stringify({
                type: 'attachment',
                length: attachmentBytes,
                filename: 'request_payload.json',
                content_type: 'application/json',
            });
            envelope += `${attachmentHeader}\n${event.attachmentPayload}\n`;
        }

        return envelope;
    }

    static #isIgnorableError(error: any): boolean {
        if (!error) return true;

        const message = error.message || error.toString?.() || '';

        return this.IGNORE_PATTERNS.some(pattern =>
            pattern instanceof RegExp ? pattern.test(message) : message.includes(pattern)
        );
    }

    /**
     * Подобрать fingerprint по сообщению ошибки из FINGERPRINT_PATTERNS.
     * null — правило не найдено, группирует Sentry по умолчанию.
     */
    static #getFingerprint(error: Error): string[] | null {
        const message = error.message || '';

        const rule = this.FINGERPRINT_PATTERNS.find(rule => {
            if (rule.pattern instanceof RegExp) {
                return rule.pattern.test(message);
            } else {
                return message.includes(rule.pattern);
            }
        });

        if (rule === undefined) {
            return null;
        } else {
            return [rule.fingerprint];
        }
    }

    static #getDsn(): string {
        return `https://${Config.sentry.publicKey}@${Config.sentry.host}/${Config.sentry.projectId}`;
    }

    static #getEnvelopeUrl(): string {
        const { host, projectId, publicKey } = Config.sentry;
        return `https://${host}/api/${projectId}/envelope/?sentry_key=${publicKey}&sentry_version=7`;
    }

    /**
     * Получить URL события в Sentry UI
     */
    static getEventUrl(eventId: string): string {
        return `https://${Config.sentry.host}/organizations/${Config.sentry.organization}/issues/?query=${eventId}`;
    }

    /**
     * Синоним reportError — единый API с бэкендом (Core::logException)
     */
    static logException(error: Error): Promise<void> {
        return this.reportError(error);
    }

    /**
     * Добавить trace-крошку (синоним SentryBreadcrumbList.trace)
     * Использование: Core.sentry.trace('message', payload)
     */
    static trace(message: string, payload?: any): void {
        SentryBreadcrumbList.trace(message, payload);
    }

    /**
     * Добавить warn-крошку с уровнем Warning
     */
    static traceWarn(message: string, payload?: any): void {
        SentryBreadcrumbList.traceWarn(message, payload);
    }
}

export default SentryService;